import type {
  LoginPlayer,
  ServerChatSayMessage,
  ServerEventBoostMessage,
  ServerEventBounceMessage,
  ServerEventStealthMessage,
  ServerPlayerFireMessage,
  ServerPlayerFlagMessage,
  ServerPlayerKillMessage,
  ServerPlayerLevelMessage,
  ServerPlayerNewMessage,
  ServerPlayerPowerupMessage,
  ServerPlayerRespawnMessage,
  ServerPlayerTypeMessage,
} from '@airmash/protocol';
import { GAME_TYPES, MOB_TYPES, PLAYER_LEVEL_UPDATE_TYPES, PLAYER_STATUS } from '@airmash/protocol';
import type { Sprite, TextStyleOptions } from 'pixi.js';
import { BitmapText, Container, Graphics as PixiGraphics } from 'pixi.js';
import type { FlagCode } from '../generated/flags';
import { flagFromId, flagTextureName, resolveFlag } from '../services/Flags';
import { clock } from './Clock';
import { noteNameplateText as hitchNoteNameplateText } from './HitchLog';
import { DISPLAY_DEFAULTS, GAME_DEFAULTS } from './Config';
import {
  BUBBLE_FONT,
  BUBBLE_FONT_SIZE,
  LEVEL_FONT,
  LEVEL_FONT_SIZE,
  NAMEPLATE_FONT,
  NAMEPLATE_FONT_SIZE,
} from './Fonts';
import {
  HALF_PI,
  HITSHAPES_TEAM_1_COLOR,
  HITSHAPES_TEAM_1_THRUSTER,
  HITSHAPES_TEAM_2_COLOR,
  HITSHAPES_TEAM_2_THRUSTER,
  MAP_BOUNDARY_INSET,
  MAP_CENTER_X,
  MAP_CENTER_Y,
  PlaneType,
  SCALE_DEFAULT,
  SHADOW_SCALING,
  TWO_PI,
} from './Constants';
import { SHIPS, UPGRADES } from './GameData';
import { layers } from './Layers';
import { showLevelUp as gamesShowLevelUp } from './Games';
import {
  inScreen as graphicsInScreen,
  shadowCoords as graphicsShadowCoords,
  shakeCamera as graphicsShakeCamera,
  transform as graphicsTransform,
  viewport,
} from './Graphics';
import { explosion as mobExplosion } from './Mobs';
import { stripBotsNamePrefix } from './NameCleanup';
import { reducedFactor as networkReducedFactor, timing as networkTiming } from './Network';
import {
  explosion as particlesExplosion,
  planeBoost as particlesPlaneBoost,
  planeDamage as particlesPlaneDamage,
} from './particles/Particles';
import { session } from './Session';
import { settings } from './Settings';
import {
  clearThruster as soundClearThruster,
  playerImpact as soundPlayerImpact,
  playerKill as soundPlayerKill,
  playerRespawn as soundPlayerRespawn,
  updatePlayerThruster as soundUpdateThruster,
} from './Sound';
import { get, init, initSprite, sprite } from './Textures';
import {
  clamp as toolsClamp,
  converge as toolsConverge,
  easing as toolsEasing,
  rand as toolsRand,
  randInt as toolsRandInt,
} from './Tools';
import type { PlayerUpdateMessage, ReelPlayer, TeamId } from './types/Players';
import type { PropOverrides } from './types/Textures';
import {
  addPowerup as uiAddPowerup,
  aircraftSelected as uiAircraftSelected,
  changeMinimapTeam as uiChangeMinimapTeam,
  getScalingFactor as uiGetScalingFactor,
  hideSpectator as uiHideSpectator,
  isEmote as uiIsEmote,
  resetPowerups as uiResetPowerups,
  updateGameInfo as uiUpdateGameInfo,
  updateMyLevel as uiUpdateMyLevel,
  visibilityHUD as uiVisibilityHUD,
  visibilityLaserPointer as uiVisibilityLaserPointer,
} from './UI';
import Vector from './Vector';

/** A remote player with no packet for this long is timed out: frozen, hidden, and no longer followed by the camera. */
/** Draws every ship's collision circles: a dev switch legacy kept in config.debug. */
const SHOW_COLLISION_CIRCLES: boolean = false;

/** The nameplate font multiplier the airmash-refugees fork added to config; nothing changes it. */
const NAMEPLATE_FONT_SCALE = 1;

export const PLAYER_TIMEOUT_MS = 3000;

// Physics constants shared by every update()
const BOOST_MULTIPLIER = 1.5;
/** A flag carrier is held to this speed whatever it flies. */
const FLAG_CARRIER_MAX_SPEED = 5;
/** Inferno (rampage) gives up a quarter of the top speed. */
const RAMPAGE_SPEED_MULTIPLIER = 0.75;
const NO_ANGLE = -999; // Sentinel value for "no movement direction"
const QUARTER_PI = Math.PI * 0.25;

// The flight envelope: the map edge, inset so the plane sprite never clips it
const MAP_MIN_X = -MAP_CENTER_X + MAP_BOUNDARY_INSET;
const MAP_MAX_X = MAP_CENTER_X - MAP_BOUNDARY_INSET;
const MAP_MIN_Y = -MAP_CENTER_Y + MAP_BOUNDARY_INSET;
const MAP_MAX_Y = MAP_CENTER_Y - MAP_BOUNDARY_INSET;

/** The bot plate's font size, smaller than a level's, which is the level font's own. */
const BOT_PLATE_FONT_SIZE = 24;

/** How far below the plane the nameplate sits, in world units at scale 1. */
const NAMEPLATE_DIST = 60;

/**
 * How many nameplate texts may change per frame across all players. Pixi
 * rasterizes a changed Text to a canvas, with the drop shadow's blur, and
 * uploads it again in that frame's render, up to a millisecond each, so a
 * scoreboard that reorders a crowded screen is spread over a few frames
 * instead of landing in one.
 */
const NAMEPLATE_TEXT_CHANGES_PER_FRAME = 2;
/** The frame the budget was last counted for, and how much of it is used. */
let nameplateTextChangesFrame = -1;
let nameplateTextChanges = 0;

/** Multiplier applied to nameplate font size when `scaleNameplates` is enabled.
 *  Softer curve than SWAM's linear `zoom/2500` — sqrt tapers growth so names
 *  stay readable when zoomed out without ballooning at extreme zoom (5000 → 1.41×,
 *  10000 → 2×). A player caches it in `state.nameplateMul` whenever its nameplate
 *  is built or rescaled, so the per-frame positioning never recomputes it. */
function nameplateZoomMul(): number {
  if (!(settings.game?.scaleNameplates ?? GAME_DEFAULTS.scaleNameplates)) return 1;
  return Math.sqrt(uiGetScalingFactor() / SCALE_DEFAULT);
}

/** The level plate's border texture is this many pixels square. */
const LEVEL_BORDER_SIZE = 32;

/** The chat bubble's centre panel texture is this many pixels square. */
const BUBBLE_PANEL_SIZE = 82;

/** The powerup icon and its ring float this far above the plane. */
const POWERUP_ICON_OFFSET_Y = 80;

/** Alpha of the powerup icon and ring at full opacity. */
const POWERUP_ICON_ALPHA = 0.75;

/** Ring tint behind the shield icon, and behind the inferno (rampage) icon. */
const POWERUP_RING_TINT_SHIELD = 0xffffff;
const POWERUP_RING_TINT_RAMPAGE = 0xff0300;

/** How a twin-engined plane draws its two flames, their shadows and their glows. */
interface DualThrusterSpec {
  /** Sine and cosine of the flames' angle off the tail, precomputed once. */
  sin: number;
  cos: number;
  /** Distance of the flames from the plane's centre. */
  dist: number;
  /** Flame sprite scale. */
  scaleX: number;
  scaleY: number;
  /** Flame shadow scale. */
  shadowX: number;
  shadowY: number;
  /** The glows sit at their own angle and distance, with their own brightness. */
  glowSin: number;
  glowCos: number;
  glowDist: number;
  glowAlpha: number;
}

/** The twin-engined planes. The Predator has one flame and the Mohawk a rotor. */
const DUAL_THRUSTERS: Record<PlaneType.Goliath | PlaneType.Tornado | PlaneType.Prowler, DualThrusterSpec> = {
  [PlaneType.Goliath]: {
    sin: Math.sin(0.5),
    cos: Math.cos(0.5),
    dist: 32,
    scaleX: 0.4,
    scaleY: 0.6,
    shadowX: 0.5,
    shadowY: 0.6,
    glowSin: Math.sin(0.3),
    glowCos: Math.cos(0.3),
    glowDist: 50,
    glowAlpha: 0.3,
  },
  [PlaneType.Tornado]: {
    sin: Math.sin(0.15),
    cos: Math.cos(0.15),
    dist: 28,
    scaleX: 0.3,
    scaleY: 0.5,
    shadowX: 0.3,
    shadowY: 0.5,
    glowSin: Math.sin(0.2),
    glowCos: Math.cos(0.2),
    glowDist: 45,
    glowAlpha: 0.25,
  },
  [PlaneType.Prowler]: {
    sin: Math.sin(0.35),
    cos: Math.cos(0.35),
    dist: 20,
    scaleX: 0.3,
    scaleY: 0.4,
    shadowX: 0.4,
    shadowY: 0.4,
    glowSin: Math.sin(0.2),
    glowCos: Math.cos(0.2),
    glowDist: 35,
    glowAlpha: 0.2,
  },
};

/** All PIXI sprites used to render the player and their effects. */
interface PlayerSprites {
  powerup: Sprite | null;
  powerupCircle: Sprite | null;
  sprite: Sprite | null;
  shadow: Sprite | null;
  thruster: Sprite | null;
  thrusterGlow: Sprite | null;
  thrusterShadow: Sprite | null;
  thruster1: Sprite | null;
  thruster2: Sprite | null;
  thruster1Glow: Sprite | null;
  thruster2Glow: Sprite | null;
  thruster1Shadow: Sprite | null;
  thruster2Shadow: Sprite | null;
  rotor: Sprite | null;
  rotorShadow: Sprite | null;
  name: BitmapText | null;
  flag: Sprite | null;
  badge: Sprite | null;
  bubble: Container | null;
  bubbleLeft: Sprite | null;
  bubbleRight: Sprite | null;
  bubbleCenter: Sprite | null;
  bubblePoint: Sprite | null;
  bubbleText: BitmapText | null;
  emote: Sprite | null;
  level: BitmapText | null;
  levelBorder: Sprite | null;
}

/** The plane's own sprites, rebuilt on a plane type change. */
const PLANE_SPRITE_KEYS = [
  'sprite',
  'shadow',
  'powerup',
  'powerupCircle',
  'thruster',
  'thrusterGlow',
  'thrusterShadow',
  'thruster1',
  'thruster2',
  'thruster1Glow',
  'thruster2Glow',
  'thruster1Shadow',
  'thruster2Shadow',
  'rotor',
  'rotorShadow',
] as const satisfies readonly (keyof PlayerSprites)[];

/** The nameplate and level plate, which outlive a plane type change and go with the player. */
const NAMEPLATE_SPRITE_KEYS = [
  'badge',
  'name',
  'flag',
  'level',
  'levelBorder',
] as const satisfies readonly (keyof PlayerSprites)[];

/** The chat bubble and its children, which are destroyed through the bubble container. */
const BUBBLE_SPRITE_KEYS = [
  'bubble',
  'bubbleLeft',
  'bubbleRight',
  'bubbleCenter',
  'bubblePoint',
  'bubbleText',
  'emote',
] as const satisfies readonly (keyof PlayerSprites)[];

/** Runtime state for animations, effects, and rendering. */
interface PlayerState {
  thrustLevel: number;
  thrustDir: number;
  bubble: boolean;
  bubbleProgress: number;
  bubbleFade: number;
  bubbleTime: number;
  bubbleTextWidth: number;
  hasBadge: boolean;
  badge: number;
  stealthLevel: number;
  scaleLevel: number;
  powerupAngle: number;
  powerupFade: number;
  powerupFadeState: number;
  lastBounceSound: number;
  /** Sprite scale of the plane type, set with its sprites. */
  baseScale: number;
  /** The zoom multiplier the nameplate was last built or rescaled with. */
  nameplateMul: number;
  nameWidth: number;
  levelWidth: number;
  /** Raised by a scoreboard rank: the nameplate text is rebuilt on the player's next rendered frame, within that frame's budget. */
  nameplateTextStale: boolean;
}

/** Player directional key input state. */
interface PlayerKeystate {
  UP: boolean;
  DOWN: boolean;
  LEFT: boolean;
  RIGHT: boolean;
}

/**
 * Parses a SAY message for the inline flag syntax: the country code between
 * `:flag` and `:` as in `:flaggb:` (vanilla), or between `-flag` and `-` as in
 * `-flaggb-` (what live StarMash sends, and the only form it renders on receive).
 * Returns the resolved FlagCode if valid, or null.
 */
function parseFlagSay(text: string): FlagCode | null {
  const delim = text[0];
  if (delim !== ':' && delim !== '-') return null;
  if (!text.startsWith(`${delim}flag`) || !text.endsWith(delim) || text.length < 7) return null;
  const code = text.slice(5, -1).toLowerCase();
  return resolveFlag(code);
}

/** The three top-rank badge sprites, by texture name. */
export type BadgeTextureName = 'badge_gold' | 'badge_silver' | 'badge_bronze';

class Player {
  id: number;
  status: PLAYER_STATUS;
  level: number | null = null;
  reel: boolean = false;
  spectate = false;
  name: string;
  type: PlaneType;
  team: TeamId;
  pos: Vector;
  lowResPos: Vector;
  speed: Vector = Vector.zero();
  speedupgrade: number = 0;
  rot: number;
  flag: FlagCode;
  sprites: PlayerSprites = {
    powerup: null,
    powerupCircle: null,
    sprite: null,
    shadow: null,
    thruster: null,
    thrusterGlow: null,
    thrusterShadow: null,
    thruster1: null,
    thruster2: null,
    thruster1Glow: null,
    thruster2Glow: null,
    thruster1Shadow: null,
    thruster2Shadow: null,
    rotor: null,
    rotorShadow: null,
    name: null,
    flag: null,
    badge: null,
    bubble: null,
    bubbleLeft: null,
    bubbleRight: null,
    bubbleCenter: null,
    bubblePoint: null,
    bubbleText: null,
    emote: null,
    level: null,
    levelBorder: null,
  };
  randomness: number = toolsRand(0, 1e5);
  keystate: PlayerKeystate = { UP: false, DOWN: false, LEFT: false, RIGHT: false };
  lastKilled: number = 0;
  rank: number = 0;
  health: number = 1;
  energy: number = 1;
  healthRegen: number = 1;
  energyRegen: number = 1;
  boost: boolean = false;
  strafe: boolean = false;
  flagspeed: boolean = false;
  stealthed: boolean = false;
  alpha: number = 1;
  scale: number = 1;
  powerups = {
    shield: false,
    rampage: false,
  };
  powerupsShown = {
    shield: false,
    rampage: false,
  };
  powerupActive: boolean = false;
  render: boolean = true;
  hidden: boolean = false;
  culled: boolean = false;
  timedout: boolean = false;
  /** Frame compensation from the last packet, consumed by the next update(); null when there is none. */
  reducedFactor: number | null = null;
  lastPacket: number;
  thrusterName: string = '';
  bot: boolean = false;
  state: PlayerState = {
    thrustLevel: 0,
    thrustDir: 0,
    bubble: false,
    bubbleProgress: 0,
    bubbleFade: 0,
    bubbleTime: 0,
    bubbleTextWidth: 0,
    hasBadge: false,
    badge: 0,
    stealthLevel: 0,
    scaleLevel: 1,
    powerupAngle: 0,
    powerupFade: 0,
    powerupFadeState: 0,
    lastBounceSound: 0,
    baseScale: 1,
    nameplateMul: 1,
    nameWidth: 0,
    levelWidth: 0,
    nameplateTextStale: false,
  };
  /** The ship's collision circles, drawn only with SHOW_COLLISION_CIRCLES. */
  col: PixiGraphics | null = null;

  // ---- Lifecycle ------------------------------------------------------------

  /** Builds the player from the packet announcing it; LOGIN lists bots apart from players, so the flag is passed in. */
  constructor(
    playerNewMsg: LoginPlayer | ServerPlayerNewMessage | ReelPlayer,
    isBot = false,
    isFromLoginPacket = false,
  ) {
    this.id = playerNewMsg.id;
    this.status = playerNewMsg.status;

    // Match original: null/0 → null, otherwise keep value
    if ('level' in playerNewMsg) {
      this.level = playerNewMsg.level == null || playerNewMsg.level === 0 ? null : playerNewMsg.level;
    }

    // The title screen's reel builds Players too; they skip the nameplate and the UI
    this.reel = 'reel' in playerNewMsg && playerNewMsg.reel === true;

    this.name = playerNewMsg.name;
    this.type = playerNewMsg.type;
    this.team = playerNewMsg.team;
    this.pos = new Vector(playerNewMsg.posX, playerNewMsg.posY);
    this.lowResPos = new Vector(playerNewMsg.posX, playerNewMsg.posY);
    this.rot = playerNewMsg.rot;
    this.flag = flagFromId(playerNewMsg.flag);
    this.lastPacket = networkTiming.timeNetwork;
    this.thrusterName = `player_${this.id}_${this.type}`;
    this.bot = isBot;
    if (this.bot) {
      this.name = stripBotsNamePrefix(this.name);
    }
    this.setupGraphics();
    if (this.status === PLAYER_STATUS.ALIVE) {
      if ('upgrades' in playerNewMsg) {
        this.decodeUpgrades(playerNewMsg.upgrades);
      }
      this.updatePowerups();
    } else {
      this.hidden = true;
      if (this.me()) {
        uiVisibilityHUD(false);
        uiVisibilityLaserPointer(false);
      }
    }
    if (!this.reel) {
      this.visibilityUpdate();
    }
    // A player who joins in view, and we ourselves, scale in from nothing; the
    // login roster appears at full size
    const isLocalPlayer = this.me();
    if ((!isFromLoginPacket && this.render) || isLocalPlayer) {
      this.scale = 0;
      this.state.scaleLevel = 0;
    }
    if (isLocalPlayer) {
      session.myType = this.type;
      uiAircraftSelected(this.type);
    }
  }

  /** Returns true if this is the local player. */
  me() {
    return session.myID === this.id;
  }

  /** Returns true if the player is in spectator mode. */
  isSpectating() {
    return this.spectate;
  }

  /** Handles player death or forced spectate, triggering explosions and camera shake. */
  kill(ev: ServerPlayerKillMessage | { posX: 0; posY: 0; spectate: true }): void {
    // Update player state to dead
    this.status = PLAYER_STATUS.INACTIVE;
    this.spectate = 'spectate' in ev;
    this.keystate.UP = false;
    this.keystate.DOWN = false;
    this.keystate.LEFT = false;
    this.keystate.RIGHT = false;
    this.pos.x = ev.posX;
    this.pos.y = ev.posY;
    this.speed.x = 0;
    this.speed.y = 0;

    // Reset powerups
    if (this.me()) {
      uiResetPowerups();
    }
    this.resetPowerups();

    // Hide player and exit stealth if active
    this.hidden = true;
    this.visibilityUpdate();
    if (this.stealthed) {
      this.unstealth();
    }

    // Clear bubble state so a mid-bubble death doesn't resurrect on respawn.
    // Sprite visibility is handled by visibilityUpdate() above.
    if (this.state.bubble) {
      this.state.bubble = false;
      this.state.bubbleFade = 0;
      this.state.bubbleProgress = 0;
    }

    // Handle death effects (not for forced spectate)
    if (!this.spectate) {
      this.lastKilled = performance.now();

      // Only show effects if player is on screen
      if (!this.culled) {
        // Spawn explosion particles (size varies by plane)
        switch (this.type) {
          case PlaneType.Predator:
            particlesExplosion(this.pos, toolsRand(1.0, 1.35), toolsRandInt(2, 3));
            break;
          case PlaneType.Goliath:
            // Larger explosion for bigger plane
            particlesExplosion(this.pos, toolsRand(1.35, 1.65), toolsRandInt(4, 7));
            break;
          case PlaneType.Mohawk:
          case PlaneType.Tornado:
          case PlaneType.Prowler:
            particlesExplosion(this.pos, toolsRand(1.0, 1.35), toolsRandInt(2, 3));
            break;
        }

        // Play death effects
        const cameraShakeIntensity = this.me() ? 20 : 10;
        graphicsShakeCamera(this.pos, cameraShakeIntensity);
        soundClearThruster(this.thrusterName);
        soundPlayerKill(this);
      }
    }
  }

  /** Destroys the plane's sprites; fullDestroy also takes the nameplate, level plate and chat bubble. */
  destroy(fullDestroy: boolean) {
    soundClearThruster(this.thrusterName);

    // Pixi's destroy() also removes the object from its layer
    for (const key of PLANE_SPRITE_KEYS) {
      this.sprites[key]?.destroy();
      this.sprites[key] = null;
    }
    if (this.col) {
      this.col.destroy();
      this.col = null;
    }

    if (fullDestroy) {
      for (const key of NAMEPLATE_SPRITE_KEYS) {
        this.sprites[key]?.destroy();
        this.sprites[key] = null;
      }
      // The panels, text and emote are the bubble's children and go with it
      this.sprites.bubble?.destroy({ children: true });
      for (const key of BUBBLE_SPRITE_KEYS) {
        this.sprites[key] = null;
      }
    }
  }

  /** Changes aircraft type, destroying and recreating sprites. */
  changeType(msg: ServerPlayerTypeMessage) {
    // Only process if type actually changed
    if (this.type === msg.type) {
      return;
    }

    // Destroy current graphics (false = don't fully remove player)
    this.destroy(false);

    // Update to new aircraft type
    this.type = msg.type;
    this.thrusterName = `player_${this.id}_${this.type}`;

    // Recreate graphics for new type
    this.setupGraphics(true);

    // Update visibility and rendering
    this.visibilityUpdate(true);
  }

  /** Handles respawn: resets all state, sets position, and triggers spawn animation. */
  respawn(msg: ServerPlayerRespawnMessage) {
    // Update network timestamp
    this.lastPacket = networkTiming.timeNetwork;

    // Reset shared state (status, movement, health, energy, stealth, visibility)
    this.revive();

    // Clear input state
    this.keystate.UP = false;
    this.keystate.DOWN = false;
    this.keystate.LEFT = false;
    this.keystate.RIGHT = false;

    // Set spawn position and rotation
    this.pos.x = msg.posX;
    this.pos.y = msg.posY;
    this.rot = msg.rot;

    // Reset velocity
    this.speed.x = 0;
    this.speed.y = 0;

    // Reset thruster animation state
    this.state.thrustLevel = 0;
    this.state.thrustDir = 0;

    // Reset timeout flag and update sprite visibility
    this.timedout = false;
    this.visibilityUpdate();

    // Reset powerup UI for local player
    if (this.me()) {
      uiResetPowerups();
    }

    // Process starting upgrades
    this.decodeUpgrades(msg.upgrades);
    this.updatePowerups();

    // Reset scale for spawn animation
    if (this.render || this.me()) {
      this.scale = 0;
      this.state.scaleLevel = 0;
    }

    // Update local player game state and UI
    if (this.me()) {
      session.myType = this.type;
      session.spectatingID = null;
      uiAircraftSelected(this.type);
      uiVisibilityHUD(true);
      // Only show laser pointer if enabled in settings
      if (settings.game?.laserPointer ?? GAME_DEFAULTS.laserPointer) {
        uiVisibilityLaserPointer(true);
      }
      uiHideSpectator();
    }

    // Update graphics and play respawn sound
    this.updateGraphics();
    soundPlayerRespawn(this);
  }

  /** Revives from dead state, resetting health/energy/movement but not position. */
  revive() {
    // Reset player status to alive
    this.status = PLAYER_STATUS.ALIVE;
    this.spectate = false;

    // Reset movement states
    this.boost = false;
    this.strafe = false;
    this.flagspeed = false;

    // Reset visibility
    this.hidden = false;

    // Reset health and energy to full
    this.health = 1;
    this.energy = 1;
    this.healthRegen = 1;
    this.energyRegen = 1;

    // Break stealth if active
    if (this.stealthed) {
      this.unstealth();
    }

    // Update UI
    uiUpdateGameInfo();
  }

  /** Times out a remote player quiet for PLAYER_TIMEOUT_MS, freezing them on the first detection. */
  detectTimeout() {
    // Local player never times out
    if (this.me()) {
      return;
    }

    // Track previous timeout state to detect transitions
    const wasTimedout = this.timedout;

    const timeSinceLastPacket = networkTiming.timeNetwork - this.lastPacket;
    this.timedout = timeSinceLastPacket > PLAYER_TIMEOUT_MS;

    // Handle newly timed out player (reset all movement)
    if (this.timedout && !wasTimedout) {
      this.resetMovementState();
    }
  }

  /** Marks remote player as timed out when leaving the visible horizon. */
  leaveHorizon() {
    // Local player never leaves horizon
    if (this.me()) {
      return;
    }

    // Already timed out, nothing to do
    if (this.timedout) {
      return;
    }

    // Mark as timed out (force timeout with old timestamp)
    this.lastPacket = -10000;
    this.timedout = true;
    this.resetMovementState();
  }

  /** Clears movement flags, velocity, input state, and powerups. */
  private resetMovementState() {
    this.boost = false;
    this.strafe = false;
    this.flagspeed = false;
    this.speed.x = 0;
    this.speed.y = 0;
    this.keystate.UP = false;
    this.keystate.DOWN = false;
    this.keystate.LEFT = false;
    this.keystate.RIGHT = false;
    this.resetPowerups();
  }

  // ---- Packets from the server ----------------------------------------------

  /**
   * Decodes the packed keystate byte into this player's input and status flags.
   *
   * Bits 0-3: UP/DOWN/LEFT/RIGHT, bit 4: boost, bit 5: strafe, bit 6: stealth, bit 7: flagspeed.
   */
  private decodeKeystate(packedKeystate: number) {
    this.keystate.UP = (packedKeystate & 1) !== 0;
    this.keystate.DOWN = (packedKeystate & 2) !== 0;
    this.keystate.LEFT = (packedKeystate & 4) !== 0;
    this.keystate.RIGHT = (packedKeystate & 8) !== 0;
    this.boost = (packedKeystate & 16) !== 0;
    this.strafe = (packedKeystate & 32) !== 0;
    this.stealthed = (packedKeystate & 64) !== 0;
    this.flagspeed = (packedKeystate & 128) !== 0;
  }

  /**
   * Decodes the packed upgrades byte into this player's upgrade state.
   *
   * Bits 0-2: speed level, bit 3: shield, bit 4: inferno, which legacy calls rampage.
   */
  private decodeUpgrades(packedUpgrades: number) {
    this.speedupgrade = packedUpgrades & 7;
    this.powerups.shield = (packedUpgrades & 8) !== 0;
    this.powerups.rampage = (packedUpgrades & 16) !== 0;
  }

  /** Stamps the packet time and revives a dead player: every per-player packet starts here. */
  private networkPreamble() {
    this.lastPacket = networkTiming.timeNetwork;
    if (this.status === PLAYER_STATUS.INACTIVE) {
      this.revive();
    }
  }

  /**
   * The original's stealth rules after a packet: an enemy we hear from is visible,
   * and a teammate, or we ourselves, uncloaks once the keystate stops saying stealthed.
   */
  private networkStealthCheck(wasStealthed: boolean) {
    if (this.team !== session.myTeam) {
      if (this.stealthed || wasStealthed) {
        this.unstealth();
      }
    } else if (wasStealthed && !this.stealthed) {
      // Same-team player (including local): restore full opacity on stealth transition out.
      // Without this, a local Prowler whose stealth is broken via keystate bits (rather than
      // an explicit EVENT_STEALTH packet) would stay at reduced opacity — thinking they're
      // still stealthed while visible to everyone else.
      this.unstealth();
    }
  }

  /** A position update, or the repel event for its owner or for a thrown player. */
  networkUpdate(msg: PlayerUpdateMessage) {
    this.networkPreamble();

    this.reducedFactor = networkReducedFactor();
    this.pos.x = msg.posX;
    this.pos.y = msg.posY;
    this.rot = msg.rot;
    this.speed.x = msg.speedX;
    this.speed.y = msg.speedY;

    // The repel event carries no keystate for its owner and no upgrades for
    // anyone. Decoding a missing one as zero would wipe the Goliath's keys and
    // freeze client-side prediction until the next real update snapped it
    // forward; legacy's null guards prevented the same.
    const wasStealthed = this.stealthed;
    if ('keystate' in msg) {
      this.decodeKeystate(msg.keystate);
    }
    if ('upgrades' in msg) {
      this.decodeUpgrades(msg.upgrades);
      this.updatePowerups();
    }
    // Only the repel event carries energy: the owner's drain for the special,
    // and each thrown player's. Without it the bar never dipped for the special.
    if ('energy' in msg) {
      this.energy = msg.energy;
      this.energyRegen = msg.energyRegen;
    }

    this.networkStealthCheck(wasStealthed);
  }

  /** Energy after a shot. */
  networkFire(msg: ServerPlayerFireMessage) {
    this.networkPreamble();

    this.energy = msg.energy;
    this.energyRegen = msg.energyRegen;

    // Firing breaks stealth, so an enemy still drawn cloaked is shown again
    this.networkStealthCheck(this.stealthed);
  }

  /** Predator boost on or off, with the position it happened at. */
  networkBoost(msg: ServerEventBoostMessage) {
    this.networkPreamble();

    this.reducedFactor = networkReducedFactor();
    this.pos.x = msg.posX;
    this.pos.y = msg.posY;
    this.rot = msg.rot;
    this.speed.x = msg.speedX;
    this.speed.y = msg.speedY;

    this.energy = msg.energy;
    this.energyRegen = msg.energyRegen;
    this.boost = msg.boost;
  }

  /** A bounce off a mountain: position, keys and an impact sound. */
  networkBounce(msg: ServerEventBounceMessage) {
    this.networkPreamble();

    this.reducedFactor = networkReducedFactor();
    this.pos.x = msg.posX;
    this.pos.y = msg.posY;
    this.rot = msg.rot;
    this.speed.x = msg.speedX;
    this.speed.y = msg.speedY;

    const wasStealthed = this.stealthed;
    this.decodeKeystate(msg.keystate);
    this.networkStealthCheck(wasStealthed);

    // Play impact sound with rate limiting
    if (clock.time - this.state.lastBounceSound > 300) {
      this.state.lastBounceSound = clock.time;
      const impactIntensity = this.speed.length() / SHIPS[this.type].maxSpeed;
      soundPlayerImpact(this.pos, this.type, impactIntensity);
    }
  }

  /** Handles stealth activation/deactivation, clearing enemy key states when entering. */
  stealth(msg: ServerEventStealthMessage) {
    // Update packet timestamp and energy state
    this.lastPacket = networkTiming.timeNetwork;
    this.energy = msg.energy;
    this.energyRegen = msg.energyRegen;

    if (msg.state) {
      // Entering stealth mode
      this.stealthed = true;
      this.state.stealthLevel = 0;

      // Clear directional keystate for enemy players (prevents prediction)
      const isEnemyPlayer = this.team !== session.myTeam;
      if (isEnemyPlayer) {
        this.keystate.LEFT = false;
        this.keystate.RIGHT = false;
      }
    } else {
      // Exiting stealth mode
      this.unstealth();
    }
  }

  /** Exits stealth mode and restores full opacity. */
  unstealth() {
    this.stealthed = false;
    this.state.stealthLevel = 0;
    this.opacity(1);
  }

  /** Handles damage: updates health, breaks stealth, creates explosion, shakes camera. */
  impact(type: MOB_TYPES, position: Vector, health: number, healthRegen: number) {
    // Update health values
    this.health = health;
    this.healthRegen = healthRegen;

    // Break stealth on impact
    if (this.stealthed) {
      this.unstealth();
    }

    // The firewall burns without a projectile to explode
    if (type !== MOB_TYPES.FIREWALL) {
      mobExplosion(position, type);
    }

    // Shake camera for alive local player
    if (this.me() && this.status === PLAYER_STATUS.ALIVE) {
      graphicsShakeCamera(position, 8);
    }
  }

  /** Forwards powerup pickup to the UI for timer display. */
  powerup(msg: ServerPlayerPowerupMessage) {
    uiAddPowerup(msg.type, msg.duration);
  }

  /** Updates the level plate, and for our own level up plays the celebration. */
  updateLevel(packet: ServerPlayerLevelMessage) {
    // Handle local player level-up UI updates
    if (this.me()) {
      // Show level-up celebration animation for normal level-ups
      if (packet.type === PLAYER_LEVEL_UPDATE_TYPES.LEVELUP) {
        gamesShowLevelUp(packet.level);
      }
      uiUpdateMyLevel(packet.level);
    }

    // Update player level and refresh level plate graphic
    this.level = packet.level;
    this.setupLevelPlate();
  }

  /** Updates the player's flag property and sprite texture. */
  changeFlag(msg: ServerPlayerFlagMessage) {
    // Update flag property (convert numeric to ISO code)
    this.flag = flagFromId(msg.flag);

    // Update flag sprite texture
    if (this.sprites.flag) {
      this.sprites.flag.texture = get(flagTextureName(this.flag))!;
    }
  }

  /** Updates the badge sprite texture. */
  changeBadge(texture: BadgeTextureName) {
    // Update badge sprite texture
    if (this.sprites.badge) {
      this.sprites.badge.texture = get(texture)!;
    }
  }

  /** Updates the player's team and refreshes team-specific visuals. */
  reteam(team: TeamId) {
    this.team = team;

    // Update nameplate color to match new team
    if (this.sprites.name) {
      this.sprites.name.style = this.nameplateTextStyle();
    }

    // Update minimap marker color
    uiChangeMinimapTeam(this.id, this.team);

    // Update aircraft tint for hitshapes theme
    this.updateTeamTint();
  }

  // ---- Sprites and appearance -----------------------------------------------

  /** Builds the plane's sprites, plus the nameplate, bubble and level plate on first setup. */
  setupGraphics(isPlaneTypeChange?: boolean) {
    let propOverrides: PropOverrides | undefined;
    if (this.me()) {
      propOverrides = {
        layer: 'aircraftme',
      };
    }
    this.sprites.powerup = init('powerupShield', {
      visible: false,
      alpha: POWERUP_ICON_ALPHA,
    });
    this.sprites.powerupCircle = init('powerupCircle', {
      visible: false,
      alpha: POWERUP_ICON_ALPHA,
    });

    switch (this.type) {
      case PlaneType.Predator:
        this.state.baseScale = 0.25;
        this.sprites.sprite = init('shipRaptor', propOverrides);
        this.sprites.shadow = init('shipRaptorShadow', {
          scale: this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        this.sprites.thruster = init('shipRaptorThruster');
        this.sprites.thrusterGlow = init('thrusterGlowSmall');
        this.sprites.thrusterShadow = init('thrusterShadow');
        break;
      case PlaneType.Goliath:
        this.state.baseScale = 0.35;
        this.sprites.sprite = init('shipSpirit', propOverrides);
        this.sprites.shadow = init('shipSpiritShadow', {
          scale: this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        this.setupDualThrusters();
        break;
      case PlaneType.Mohawk:
        this.state.baseScale = 0.25;
        this.sprites.sprite = init('shipComanche', propOverrides);
        this.sprites.rotor = init('shipComancheRotor', propOverrides);
        this.sprites.shadow = init('shipComancheShadow', {
          scale: this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        this.sprites.rotorShadow = init('shipComancheRotorShadow', {
          scale: 2 * this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        break;
      case PlaneType.Tornado:
        this.state.baseScale = 0.28;
        this.sprites.sprite = init('shipTornado', propOverrides);
        this.sprites.shadow = init('shipTornadoShadow', {
          scale: this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        this.setupDualThrusters();
        break;
      case PlaneType.Prowler:
        this.state.baseScale = 0.28;
        this.sprites.sprite = init('shipProwler', propOverrides);
        this.sprites.shadow = init('shipProwlerShadow', {
          scale: this.state.baseScale * (2.4 / SHADOW_SCALING),
        });
        this.setupDualThrusters();
    }

    // The nameplate, bubble and level plate outlive a plane type change; the reel has none
    if (!this.reel && !isPlaneTypeChange) {
      this.setupNameplate();
      this.setupChatBubbles();
      if (this.level != null || this.bot) {
        this.setupLevelPlate();
      }
    }
    if (SHOW_COLLISION_CIRCLES) {
      this.col = new PixiGraphics();
      for (const [x, y, radius] of SHIPS[this.type].collisions) {
        this.col.circle(x, y, radius).fill({ color: 0xffffff, alpha: 0.2 });
      }
      layers.explosions.addChild(this.col);
    }

    // Apply team-based tint for hitshapes theme
    this.updateTeamTint();
  }

  /** The twin flames, glows and shadows of the Goliath, Tornado and Prowler. */
  private setupDualThrusters() {
    this.sprites.thruster1 = init('shipRaptorThruster');
    this.sprites.thruster2 = init('shipRaptorThruster');
    this.sprites.thruster1Glow = init('thrusterGlowSmall');
    this.sprites.thruster2Glow = init('thrusterGlowSmall');
    this.sprites.thruster1Shadow = init('thrusterShadow');
    this.sprites.thruster2Shadow = init('thrusterShadow');
  }

  /** Applies team-based tint for hitshapes theme in CTF mode. */
  updateTeamTint() {
    // Only apply team tints for hitshapes theme in CTF mode
    const theme = settings.display?.theme ?? DISPLAY_DEFAULTS.theme;
    if (theme !== 'hitshapes' || session.gameType !== GAME_TYPES.CTF) {
      return;
    }

    if (!this.sprites.sprite) {
      return;
    }

    const playerTint = this.team === 1 ? HITSHAPES_TEAM_1_COLOR : HITSHAPES_TEAM_2_COLOR;
    const thrusterTint = this.team === 1 ? HITSHAPES_TEAM_1_THRUSTER : HITSHAPES_TEAM_2_THRUSTER;

    this.sprites.sprite.tint = playerTint;

    // Also tint the rotor for Mohawk (Comanche)
    if (this.sprites.rotor) {
      this.sprites.rotor.tint = playerTint;
    }

    // Tint thrusters with lighter team color
    if (this.sprites.thruster) this.sprites.thruster.tint = thrusterTint;
    if (this.sprites.thruster1) this.sprites.thruster1.tint = thrusterTint;
    if (this.sprites.thruster2) this.sprites.thruster2.tint = thrusterTint;
    if (this.sprites.thrusterGlow) this.sprites.thrusterGlow.tint = thrusterTint;
    if (this.sprites.thruster1Glow) this.sprites.thruster1Glow.tint = thrusterTint;
    if (this.sprites.thruster2Glow) this.sprites.thruster2Glow.tint = thrusterTint;
  }

  /** Creates nameplate text, flag icon, and badge sprites. */
  setupNameplate() {
    const mul = nameplateZoomMul();
    this.state.nameplateMul = mul;

    // Create nameplate text with player name (extended format applied by updateNameplateText)
    this.sprites.name = new BitmapText({
      text: this.name,
      style: this.nameplateTextStyle(),
    });
    this.sprites.name.scale.set(0.5, 0.5);

    // Create country flag sprite (this.flag is already ISO code)
    this.sprites.flag = sprite(flagTextureName(this.flag));
    this.sprites.flag.scale.set(0.4 * mul, 0.4 * mul);
    this.sprites.flag.anchor.set(0.5, 0.5);

    // Create badge sprite (hidden by default, shown for top 3 players)
    this.sprites.badge = sprite('badge_gold');
    this.sprites.badge.scale.set(0.3 * mul);
    this.sprites.badge.visible = false;

    // Add all nameplate elements to the rendering layer
    layers.playernames.addChild(this.sprites.badge);
    layers.playernames.addChild(this.sprites.flag);
    layers.playernames.addChild(this.sprites.name);

    // Apply extended nameplate format immediately (rank 0 until scoreboard update)
    this.updateNameplateText(this.rank);
  }

  /** The nameplate's style, coloured by team and game mode: the installed font at the zoomed size, the colour as its tint. */
  nameplateTextStyle(): TextStyleOptions {
    let color: string;

    if (session.gameType === GAME_TYPES.CTF) {
      // CTF mode: blue vs red teams
      color = this.team === 1 ? '#4076E2' : '#EA4242';
    } else {
      // FFA/BTR mode: white for friendly, yellow for enemy
      color = this.team === session.myTeam ? '#FFFFFF' : '#FFEC52';
    }

    return {
      fontFamily: NAMEPLATE_FONT,
      fontSize: NAMEPLATE_FONT_SIZE * NAMEPLATE_FONT_SCALE * this.state.nameplateMul,
      fill: color,
    };
  }

  /** The nameplate's text: the name alone, or with the rank prefix and the teammate health bracket. */
  private nameplateText(rank: number): string {
    // Check if extended nameplates are enabled (default true)
    const extendedEnabled = settings.game?.extendedNameplates ?? GAME_DEFAULTS.extendedNameplates;
    if (!extendedEnabled) {
      return this.name;
    }

    // Extended nameplate: rank + name + optional health for teammates.
    // Both elements have their own sub-toggle (default on) so players can mix
    // them — e.g. SWAM-parity is "rank. name" with the health bracket off.
    const isTeammate = this.team === session.myTeam && this.id !== session.myID;
    const showHealth = settings.game?.teammateHealth ?? GAME_DEFAULTS.teammateHealth;
    const healthPart = isTeammate && showHealth ? ' [' + Math.floor(this.health * 100) + ']' : '';

    // Rank prefix only when enabled and we have a valid rank (> 0)
    const showRank = settings.game?.nameplateRank ?? GAME_DEFAULTS.nameplateRank;
    const rankPrefix = showRank && rank > 0 ? rank + '. ' : '';
    return rankPrefix + this.name + healthPart;
  }

  /** Sets the nameplate text now, for setup and a settings change; a scoreboard rank goes through setRank. */
  updateNameplateText(rank: number) {
    this.rank = rank;
    this.state.nameplateTextStale = false;

    if (!this.sprites.name) {
      return;
    }

    this.sprites.name.text = this.nameplateText(rank);
    this.state.nameWidth = this.sprites.name.width;
    hitchNoteNameplateText();
  }

  /**
   * Records the scoreboard rank and leaves the text to applyStaleNameplateText
   * on the player's next rendered frame. A scoreboard ranks every player and a
   * kill reorders neighbors, and legacy never rewrote a nameplate after
   * creating it, so the rewrite is kept out of the packet's task, skipped
   * while the player is off screen and spread over frames when it is not.
   */
  setRank(rank: number) {
    this.rank = rank;
    this.state.nameplateTextStale = true;
  }

  /** Rebuilds a stale nameplate text: an unchanged string is free, a changed one takes one of the frame's slots or waits for the next frame. */
  private applyStaleNameplateText() {
    if (!this.state.nameplateTextStale || !this.sprites.name) {
      return;
    }

    const text = this.nameplateText(this.rank);
    if (text !== this.sprites.name.text) {
      if (nameplateTextChangesFrame !== clock.frames) {
        nameplateTextChangesFrame = clock.frames;
        nameplateTextChanges = 0;
      }
      if (nameplateTextChanges >= NAMEPLATE_TEXT_CHANGES_PER_FRAME) {
        return;
      }
      nameplateTextChanges++;
      this.sprites.name.text = text;
      this.state.nameWidth = this.sprites.name.width;
      hitchNoteNameplateText();
    }
    this.state.nameplateTextStale = false;
  }

  /** Re-applies nameplate font sizes and sprite scales for the current zoom + setting.
   *  Covers name text, flag, badge, level/bot text, and level border so the whole
   *  nameplate group scales together. */
  refreshNameplateScale() {
    const mul = nameplateZoomMul();
    this.state.nameplateMul = mul;
    if (this.sprites.name) {
      this.sprites.name.style = this.nameplateTextStyle();
      this.state.nameWidth = this.sprites.name.width;
    }
    if (this.sprites.flag) {
      this.sprites.flag.scale.set(0.4 * mul, 0.4 * mul);
    }
    if (this.sprites.badge) {
      this.sprites.badge.scale.set(0.3 * mul);
    }
    if (this.sprites.level) {
      // Reassign the whole style, as for the name text; the glyphs are laid
      // out again at the new size, nothing is rasterised.
      this.sprites.level.style = this.levelTextStyle();
      this.fitLevelBorder();
    }
  }

  /** Creates or updates the level/bot plate next to the player's name. */
  setupLevelPlate() {
    const plateText = this.bot ? 'bot' : this.level + '';

    // Initialize level sprites if not already created
    if (this.sprites.level == null) {
      this.sprites.level = new BitmapText({
        text: plateText,
        style: this.levelTextStyle(),
      });
      this.sprites.level.scale.set(0.5, 0.5);

      // Create border sprite
      this.sprites.levelBorder = sprite('levelborder');
      this.sprites.levelBorder.alpha = 0.4;

      // Add to rendering layer
      layers.playernames.addChild(this.sprites.levelBorder);
      layers.playernames.addChild(this.sprites.level);
    } else {
      // Update existing text
      this.sprites.level.text = plateText;
    }

    this.fitLevelBorder();

    // Update visibility based on render flag
    this.sprites.level.visible = this.render;
    if (this.sprites.levelBorder) {
      this.sprites.levelBorder.visible = this.render;
    }
  }

  /** The level plate's text style at the nameplate's current zoom multiplier. */
  private levelTextStyle(): TextStyleOptions {
    const baseFontSize = this.bot ? BOT_PLATE_FONT_SIZE : LEVEL_FONT_SIZE;
    return {
      fontFamily: LEVEL_FONT,
      fontSize: baseFontSize * NAMEPLATE_FONT_SCALE * this.state.nameplateMul,
      fill: 0xc8c8c8,
    };
  }

  /** Measures the level text and stretches the border's 9-slice to fit it. */
  private fitLevelBorder() {
    if (!this.sprites.level) {
      return;
    }
    this.state.levelWidth = this.sprites.level.width;
    const borderWidth = (this.state.levelWidth + 10) / LEVEL_BORDER_SIZE;
    this.sprites.levelBorder?.scale.set(borderWidth, 0.65 * this.state.nameplateMul);
  }

  /** Creates chat bubble container with 9-slice background, text, and emote sprites. */
  setupChatBubbles() {
    // Create container to hold all chat bubble elements
    this.sprites.bubble = new Container();

    // Create bubble background panels (9-slice style)
    this.sprites.bubbleLeft = initSprite('chatbubbleleft', this.sprites.bubble, { scale: 0.5 });
    this.sprites.bubbleRight = initSprite('chatbubbleright', this.sprites.bubble, { scale: 0.5 });
    this.sprites.bubbleCenter = initSprite('chatbubblecenter', this.sprites.bubble, { scale: 0.5 });
    this.sprites.bubblePoint = initSprite('chatbubblepoint', this.sprites.bubble, { scale: 0.5 });

    // Icon for an emote or a flag
    this.sprites.emote = initSprite('emote_tf', this.sprites.bubble, {
      scale: 0.6,
      anchor: [0.5, 0.5],
    });

    // Create text field for chat message content
    this.sprites.bubbleText = new BitmapText({
      text: 'a',
      style: {
        fontFamily: BUBBLE_FONT,
        fontSize: BUBBLE_FONT_SIZE * NAMEPLATE_FONT_SCALE,
        fill: 0xffffff,
      },
    });
    this.sprites.bubble.addChild(this.sprites.bubbleText);

    // Configure bubble visibility and positioning
    this.sprites.bubble.visible = false;
    this.sprites.bubble.pivot.set(0.5, 34);

    // Add bubble container to the bubbles rendering layer
    layers.bubbles.addChild(this.sprites.bubble);
  }

  /** Displays a chat message, an emote or a flag in a speech bubble above the player. */
  sayBubble(msg: ServerChatSayMessage) {
    // Update bubble timing for fade-out
    this.state.bubbleTime = clock.time;
    this.state.bubbleFade = 0;

    // Initialize bubble if not already showing
    if (!this.state.bubble) {
      this.state.bubble = true;
      this.state.bubbleProgress = 0;
      if (this.sprites.bubble) {
        this.sprites.bubble.visible = this.render;
        this.sprites.bubble.alpha = 0;
        this.sprites.bubble.scale.set(0, 0);
      }
    }

    // An inline flag (:flaggb: in vanilla, -flaggb- from live StarMash) is
    // checked before the emotes so it is never mistaken for one. Text keeps the
    // original's panel height; an emote gets a squarer bubble, and a flag a
    // slightly shorter one for its 64x48 aspect against the emotes' 64x64.
    const flagCode = parseFlagSay(msg.text);
    const emoteType = flagCode ? null : uiIsEmote(msg.text, true);
    const icon = flagCode ? get(flagTextureName(flagCode)) : emoteType ? get(`emote_${emoteType}`) : undefined;
    const bubbleHeightScale = icon == null ? 0.5 : flagCode ? 0.6 : 0.7;
    let bubbleWidth = icon == null ? 0 : flagCode ? 32 : 26;

    // baseY is the top of the bubble; scaling Y grows the bubble downward from it.
    // The point sits just below the bottom of the centre panel (was y=18 for height 41).
    const baseY = -21;
    const centerHeight = BUBBLE_PANEL_SIZE * bubbleHeightScale;
    const bubbleCenterY = baseY + centerHeight / 2;
    const pointY = baseY + centerHeight - 3;

    if (icon != null) {
      if (this.sprites.bubbleText) this.sprites.bubbleText.visible = false;
      if (this.sprites.emote) {
        this.sprites.emote.texture = icon;
        this.sprites.emote.visible = true;
        this.sprites.emote.position.set(0, bubbleCenterY);
      }
    } else {
      if (this.sprites.emote) this.sprites.emote.visible = false;
      if (this.sprites.bubbleText) {
        this.sprites.bubbleText.visible = true;
        this.sprites.bubbleText.text = msg.text;
        bubbleWidth = this.sprites.bubbleText.width;
        // The text's anchor is its top left, so offset by half its height to centre it
        this.sprites.bubbleText.position.set(-bubbleWidth / 2, bubbleCenterY - this.sprites.bubbleText.height / 2);
      }
    }

    // Lay the 9-slice panels out around the content
    if (this.sprites.bubbleLeft) {
      this.sprites.bubbleLeft.position.set(-bubbleWidth / 2 - 16, baseY);
      this.sprites.bubbleLeft.scale.set(0.5, bubbleHeightScale);
    }
    if (this.sprites.bubbleRight) {
      this.sprites.bubbleRight.position.set(bubbleWidth / 2 + 8, baseY);
      this.sprites.bubbleRight.scale.set(0.5, bubbleHeightScale);
    }
    if (this.sprites.bubbleCenter) {
      this.sprites.bubbleCenter.position.set(-bubbleWidth / 2 - 9, baseY);
      this.sprites.bubbleCenter.scale.set(bubbleWidth / BUBBLE_PANEL_SIZE + 18 / BUBBLE_PANEL_SIZE, bubbleHeightScale);
    }
    if (this.sprites.bubblePoint) {
      this.sprites.bubblePoint.position.set(-9, pointY);
    }

    // Store width for animation calculations
    this.state.bubbleTextWidth = bubbleWidth;
  }

  /** Updates visibility of all sprites based on culling, hidden, and timeout state. */
  visibilityUpdate(force = false) {
    // Check if player is outside the visible screen area
    this.culled = !graphicsInScreen(this.pos, 128);

    // Calculate overall visibility (visible if not hidden, culled, or timed out)
    const isVisible = !(this.hidden || this.culled || this.timedout);

    // Only update sprites if visibility changed or forced
    const shouldUpdate = force || this.render !== isVisible;
    if (!shouldUpdate) {
      return;
    }

    // Update core sprite visibility
    if (this.sprites.sprite) this.sprites.sprite.visible = isVisible;
    if (this.sprites.shadow) this.sprites.shadow.visible = isVisible;
    if (this.sprites.flag) this.sprites.flag.visible = isVisible;
    if (this.sprites.name) this.sprites.name.visible = isVisible;

    // Update level plate visibility (if player has one)
    if (this.sprites.level && this.sprites.levelBorder) {
      this.sprites.level.visible = isVisible;
      this.sprites.levelBorder.visible = isVisible;
    }

    // Update conditional sprite visibility
    if (this.sprites.badge) {
      this.sprites.badge.visible = this.state.hasBadge && isVisible;
    }
    if (this.sprites.powerup) {
      this.sprites.powerup.visible = this.powerupActive && isVisible;
    }
    if (this.sprites.powerupCircle) {
      this.sprites.powerupCircle.visible = this.powerupActive && isVisible;
    }
    if (this.sprites.bubble) {
      this.sprites.bubble.visible = this.state.bubble && isVisible;
    }

    // Update plane-specific thruster/rotor visibility
    switch (this.type) {
      case PlaneType.Predator:
        // Single thruster configuration
        if (this.sprites.thruster) this.sprites.thruster.visible = isVisible;
        if (this.sprites.thrusterGlow) this.sprites.thrusterGlow.visible = isVisible;
        if (this.sprites.thrusterShadow) this.sprites.thrusterShadow.visible = isVisible;
        break;

      case PlaneType.Goliath:
      case PlaneType.Tornado:
      case PlaneType.Prowler:
        // Dual thruster configuration
        if (this.sprites.thruster1) this.sprites.thruster1.visible = isVisible;
        if (this.sprites.thruster1Glow) this.sprites.thruster1Glow.visible = isVisible;
        if (this.sprites.thruster1Shadow) this.sprites.thruster1Shadow.visible = isVisible;
        if (this.sprites.thruster2) this.sprites.thruster2.visible = isVisible;
        if (this.sprites.thruster2Glow) this.sprites.thruster2Glow.visible = isVisible;
        if (this.sprites.thruster2Shadow) this.sprites.thruster2Shadow.visible = isVisible;
        break;

      case PlaneType.Mohawk:
        // Rotor configuration (helicopter-style)
        if (this.sprites.rotor) this.sprites.rotor.visible = isVisible;
        if (this.sprites.rotorShadow) this.sprites.rotorShadow.visible = isVisible;
        break;
    }

    // Update render state and clear sounds when becoming invisible
    this.render = isVisible;
    if (!isVisible) {
      soundClearThruster(this.thrusterName);
    }
  }

  /** Sets opacity for all player sprites (powerups at 75%, level border at 40%). */
  opacity(alpha: number) {
    // Update player's overall alpha value
    this.alpha = alpha;

    // Update core sprite alpha values
    if (this.sprites.sprite) this.sprites.sprite.alpha = alpha;
    if (this.sprites.shadow) this.sprites.shadow.alpha = alpha;
    if (this.sprites.flag) this.sprites.flag.alpha = alpha;
    if (this.sprites.name) this.sprites.name.alpha = alpha;
    if (this.sprites.badge) this.sprites.badge.alpha = alpha;

    // Powerup sprites use reduced alpha (75%) for visual balance
    if (this.sprites.powerup) this.sprites.powerup.alpha = POWERUP_ICON_ALPHA * alpha;
    if (this.sprites.powerupCircle) this.sprites.powerupCircle.alpha = POWERUP_ICON_ALPHA * alpha;

    // Update level plate alpha (if player has one)
    if (this.sprites.level && this.sprites.levelBorder) {
      this.sprites.level.alpha = alpha;
      this.sprites.levelBorder.alpha = 0.4 * alpha; // Border is more subtle
    }

    // Prowler-specific: update thruster alpha
    if (this.type === PlaneType.Prowler) {
      if (this.sprites.thruster1) this.sprites.thruster1.alpha = alpha;
      if (this.sprites.thruster1Glow) this.sprites.thruster1Glow.alpha = alpha;
      if (this.sprites.thruster2) this.sprites.thruster2.alpha = alpha;
      if (this.sprites.thruster2Glow) this.sprites.thruster2Glow.alpha = alpha;
    }
  }

  /** Resets all powerup flags, hides sprites, and clears animation state. */
  resetPowerups() {
    // Hide powerup graphics only if currently active
    if (this.powerupActive) {
      if (this.sprites.powerup) {
        this.sprites.powerup.visible = false;
      }
      if (this.sprites.powerupCircle) {
        this.sprites.powerupCircle.visible = false;
      }
    }

    // Always reset shield powerup state (even if not currently active)
    this.powerups.shield = false;
    this.powerupsShown.shield = false;

    // Always reset rampage powerup state
    this.powerups.rampage = false;
    this.powerupsShown.rampage = false;

    // Always reset powerup active flag and animation state
    this.powerupActive = false;
    this.state.powerupFade = 0;
    this.state.powerupFadeState = 0;
  }

  /** Updates powerup sprites and fade animations when shield/rampage state changes. */
  updatePowerups() {
    let powerupChanged = false;

    // Check if shield powerup state changed
    if (this.powerups.shield !== this.powerupsShown.shield) {
      this.powerupsShown.shield = this.powerups.shield;

      if (this.powerups.shield && this.sprites.powerup && this.sprites.powerupCircle) {
        // Activate shield powerup graphics
        this.sprites.powerup.texture = get('powerup_shield')!;
        this.sprites.powerupCircle.tint = POWERUP_RING_TINT_SHIELD;
      }

      powerupChanged = true;
    }

    // Check if rampage powerup state changed
    if (this.powerups.rampage !== this.powerupsShown.rampage) {
      this.powerupsShown.rampage = this.powerups.rampage;

      if (this.powerups.rampage && this.sprites.powerup && this.sprites.powerupCircle) {
        // Activate rampage powerup graphics
        this.sprites.powerup.texture = get('powerup_rampage')!;
        this.sprites.powerupCircle.tint = POWERUP_RING_TINT_RAMPAGE;
      }

      powerupChanged = true;
    }

    // Update powerup animation state if anything changed
    if (powerupChanged) {
      const anyPowerupActive = this.powerups.shield || this.powerups.rampage;
      this.powerupActive = anyPowerupActive;

      if (anyPowerupActive) {
        // Powerup activating - fade in
        this.state.powerupFade = 0;
        this.state.powerupFadeState = 0; // Fade in

        // Gate on the player's own render state rather than forcing `true`.
        // visibilityUpdate() early-returns while render state is unchanged and
        // updateGraphics() skips transforms for invisible players, so an icon
        // shown here for an off-screen player is never repositioned OR hidden
        // again — it strands at the activation position (a CTF spawn shield on
        // a respawn at a base you aren't looking at). visibilityUpdate() turns
        // it on from `powerupActive` the moment the player becomes visible.
        // (The original had no updateGraphics early-return, so its icon kept
        // tracking the plane while invisible and never stranded.)
        if (this.sprites.powerup) {
          this.sprites.powerup.visible = this.render;
          // Position sprite immediately to prevent it from appearing at map center (0,0)
          this.sprites.powerup.position.set(this.pos.x, this.pos.y - POWERUP_ICON_OFFSET_Y);
        }
        if (this.sprites.powerupCircle) {
          this.sprites.powerupCircle.visible = this.render;
          // Position sprite immediately to prevent it from appearing at map center (0,0)
          this.sprites.powerupCircle.position.set(this.pos.x, this.pos.y - POWERUP_ICON_OFFSET_Y);
        }
      } else {
        // Powerup expiring - fade out (keep active for animation)
        this.powerupActive = true;
        this.state.powerupFade = 0;
        this.state.powerupFadeState = 1; // Fade out
      }
    }
  }

  // ---- Every frame ----------------------------------------------------------

  /** Eases the thruster flame toward the keys, or spins the Mohawk rotor, by `step` frames. */
  private convergeThrusters(step: number) {
    switch (this.type) {
      case PlaneType.Predator:
      case PlaneType.Goliath:
      case PlaneType.Tornado:
      case PlaneType.Prowler: {
        const boostMul = this.boost ? BOOST_MULTIPLIER : 1;
        const targetDirection = this.keystate.LEFT ? 0.3 : this.keystate.RIGHT ? -0.3 : 0;
        this.state.thrustDir = toolsConverge(this.state.thrustDir, targetDirection, 0.1 * step);
        const targetIntensity = this.keystate.UP ? 1 : this.keystate.DOWN ? -1 : 0;
        this.state.thrustLevel = toolsConverge(this.state.thrustLevel, targetIntensity * boostMul, 0.2 * step);
        break;
      }
      case PlaneType.Mohawk: {
        const rotationSpeed = 0.2 + this.speed.length() / 50;
        this.state.thrustDir += rotationSpeed * step;
        break;
      }
    }
  }

  /** Per-frame update: physics, movement, regeneration, boundary clamping, and visuals. */
  update(elapsedFrames: number) {
    // Reel mode uses simplified calculations (no substep loop)
    if (this.reel) {
      this.convergeThrusters(elapsedFrames);
      this.clientCalcs(elapsedFrames);
      return;
    }

    // Detect network timeouts
    this.detectTimeout();

    // Update sprite visibility
    this.visibilityUpdate();

    // Non-rendered players only update health
    if (!this.render) {
      this.health += elapsedFrames * this.healthRegen;
      if (this.health >= 1) {
        this.health = 1;
      }
      return;
    }

    // Apply frame compensation from network sync. A negative factor, from a
    // packet that ran ahead of the frame clock, lengthens the step as the
    // original client did.
    let adjustedFrames = elapsedFrames;
    if (this.reducedFactor !== null) {
      adjustedFrames = elapsedFrames - this.reducedFactor;
      this.reducedFactor = null;

      // Skip physics if compensated time is negative
      if (adjustedFrames <= 0) {
        return;
      }
    }

    // === Physics Loop ===
    // Split large time deltas into smaller iterations for stability
    const roundedFrames = adjustedFrames > 0.51 ? Math.round(adjustedFrames) : 1;
    const perLoopEffect = adjustedFrames / roundedFrames;

    // Hoist config lookups outside loop for performance
    const { turnFactor, accelFactor, maxSpeed: maxSpeedBase, minSpeed, brakeFactor } = SHIPS[this.type];
    const boostMul = this.boost ? BOOST_MULTIPLIER : 1;

    // Hoist upgrade factor (was looked up each iteration)
    const speedUpgradeFactor = UPGRADES.speed.factor[this.speedupgrade] ?? 1;

    // Pre-compute max speed with all modifiers
    let maxSpeedRaw = maxSpeedBase * boostMul * speedUpgradeFactor;
    if (this.powerups.rampage) {
      maxSpeedRaw *= RAMPAGE_SPEED_MULTIPLIER;
    }
    const maxSpeed = this.flagspeed ? FLAG_CARRIER_MAX_SPEED : maxSpeedRaw;
    const maxSpeedSq = maxSpeed * maxSpeed; // For sqrt-free comparison
    const accelPerStep = accelFactor * perLoopEffect * boostMul;
    const brakeRetain = 1 - brakeFactor * perLoopEffect;

    // Run physics substeps
    for (let i = 0; i < roundedFrames; i++) {
      // === Energy Regeneration ===
      this.energy += perLoopEffect * this.energyRegen;
      if (this.energy >= 1) {
        this.energy = 1;
      }

      // === Health Regeneration ===
      this.health += perLoopEffect * this.healthRegen;
      if (this.health >= 1) {
        this.health = 1;
      }

      // === Rotation and Strafing ===
      let speedDeltaAngle = NO_ANGLE;

      if (this.strafe) {
        // Strafe mode: move sideways
        if (this.keystate.LEFT) {
          speedDeltaAngle = this.rot - HALF_PI; // 90° left
        }
        if (this.keystate.RIGHT) {
          speedDeltaAngle = this.rot + HALF_PI; // 90° right
        }
      } else {
        // Normal mode: rotate plane
        if (this.keystate.LEFT) {
          this.rot += -perLoopEffect * turnFactor;
        }
        if (this.keystate.RIGHT) {
          this.rot += perLoopEffect * turnFactor;
        }
      }

      // === Movement Direction ===
      // Store previous velocity for acceleration-aware integration
      const prevSpeedX = this.speed.x;
      const prevSpeedY = this.speed.y;

      if (this.keystate.UP) {
        if (speedDeltaAngle === NO_ANGLE) {
          speedDeltaAngle = this.rot; // Forward
        } else {
          // Diagonal (strafe + forward)
          speedDeltaAngle += this.keystate.RIGHT ? -QUARTER_PI : QUARTER_PI;
        }
      } else if (this.keystate.DOWN) {
        if (speedDeltaAngle === NO_ANGLE) {
          speedDeltaAngle = this.rot + Math.PI; // Backward
        } else {
          // Diagonal (strafe + backward)
          speedDeltaAngle += this.keystate.RIGHT ? QUARTER_PI : -QUARTER_PI;
        }
      }

      // === Apply Acceleration ===
      if (speedDeltaAngle !== NO_ANGLE) {
        this.speed.x += Math.sin(speedDeltaAngle) * accelPerStep;
        this.speed.y -= Math.cos(speedDeltaAngle) * accelPerStep;
      }

      // === Speed Limiting and Braking ===
      // Use squared comparison to avoid expensive sqrt in common case
      const speedSq = this.speed.lengthSq();

      if (speedSq > maxSpeedSq) {
        // Above max speed - clamp (only now do we need sqrt)
        const curSpeed = Math.sqrt(speedSq);
        const mul = maxSpeed / curSpeed;
        this.speed.x *= mul;
        this.speed.y *= mul;
      } else if (
        this.speed.x > minSpeed ||
        this.speed.x < -minSpeed ||
        this.speed.y > minSpeed ||
        this.speed.y < -minSpeed
      ) {
        // Apply braking friction
        this.speed.x *= brakeRetain;
        this.speed.y *= brakeRetain;
      } else {
        // Stop completely at low speeds
        this.speed.x = 0;
        this.speed.y = 0;
      }

      // === Position Update (Acceleration-Aware Integration) ===
      // pos += dt*v0 + 0.5*(v1-v0)*dt² accounts for velocity change within the step
      this.pos.x += perLoopEffect * prevSpeedX + 0.5 * (this.speed.x - prevSpeedX) * perLoopEffect * perLoopEffect;
      this.pos.y += perLoopEffect * prevSpeedY + 0.5 * (this.speed.y - prevSpeedY) * perLoopEffect * perLoopEffect;

      // === Thruster/Rotor Convergence (inside substep for non-linear accuracy) ===
      this.convergeThrusters(perLoopEffect);
    }

    // === Post-Loop Updates ===

    // Client-side visuals run once per frame, not per substep. Everything in
    // them is linear in time except the particle emitters, which add one
    // particle per call: this spawns one per rendered frame where the original
    // spawned one per simulated frame.
    this.clientCalcs(adjustedFrames);

    // Normalize rotation to [0, 2π]
    this.rot = ((this.rot % TWO_PI) + TWO_PI) % TWO_PI;

    // === Map Boundary ===
    // Every game type clamps to the map; the original's wrap branch keyed on a
    // game type it never set.
    if (this.pos.x < MAP_MIN_X) this.pos.x = MAP_MIN_X;
    if (this.pos.x > MAP_MAX_X) this.pos.x = MAP_MAX_X;
    if (this.pos.y < MAP_MIN_Y) this.pos.y = MAP_MIN_Y;
    if (this.pos.y > MAP_MAX_Y) this.pos.y = MAP_MAX_Y;

    // Update thruster sound
    soundUpdateThruster(this);
  }

  /** Client-side visuals: particles, stealth, spawn scale, powerup fade. */
  clientCalcs(elapsedFrames: number) {
    // Nothing to animate for a player who is off screen or not drawn
    if (this.culled || !this.render) {
      return;
    }

    // Boost smoke trails the flame forward or back; the Mohawk has no flame
    const thrustIntensity = this.type !== PlaneType.Mohawk ? (this.keystate.UP ? 1 : this.keystate.DOWN ? -1 : 0) : 0;

    // === Damage Particle Effects ===
    if (!this.stealthed) {
      // Moderate damage (below 40% health)
      if (this.health < 0.4) {
        particlesPlaneDamage(this);
      }
      // Heavy damage (below 20% health) - extra particles
      if (this.health < 0.2) {
        particlesPlaneDamage(this);
      }
    }

    // === Boost Particle Effects ===
    if (this.boost) {
      const isForwardThrust = thrustIntensity >= 0;
      particlesPlaneBoost(this, isForwardThrust);
    }

    // === Prowler Stealth Opacity ===
    if (this.type === PlaneType.Prowler && this.stealthed) {
      // Gradually increase stealth level
      this.state.stealthLevel += 0.03 * elapsedFrames;

      // Clamp based on team (friendlies are more visible)
      const maxStealthLevel = this.team === session.myTeam ? 0.5 : 1;
      this.state.stealthLevel = toolsClamp(this.state.stealthLevel, 0, maxStealthLevel);

      // Update opacity (higher stealth = lower opacity)
      this.opacity(1 - this.state.stealthLevel);
    }

    // === Spawn Scale-In Animation ===
    this.state.scaleLevel += 0.005 * elapsedFrames;
    if (this.state.scaleLevel >= 1) {
      this.state.scaleLevel = 1;
      this.scale = 1;
    } else {
      // Elastic bounce effect during spawn
      this.scale = toolsEasing.outElastic(this.state.scaleLevel, 0.5);
    }

    // === Powerup Icon Animation ===
    if (this.powerupActive) {
      // Rotate powerup icon
      this.state.powerupAngle += 0.075 * elapsedFrames;

      if (this.state.powerupFadeState === 0) {
        // Fade in (powerup activating)
        this.state.powerupFade += 0.05 * elapsedFrames;
        if (this.state.powerupFade >= 1) {
          this.state.powerupFade = 1;
        }
      } else {
        // Fade out (powerup expiring)
        this.state.powerupFade += 0.05 * elapsedFrames;
        if (this.state.powerupFade >= 1) {
          // Hide powerup when fully faded
          this.powerupActive = false;
          if (this.sprites.powerup) {
            this.sprites.powerup.visible = false;
          }
          if (this.sprites.powerupCircle) {
            this.sprites.powerupCircle.visible = false;
          }
        }
      }
    }
  }

  /** Transforms all sprites: aircraft, shadow, thrusters, powerups, nameplate, bubble. */
  updateGraphics() {
    // Early return for non-visible players to skip expensive calculations.
    // Sprites are already set to invisible via visibilityUpdate() — but a few
    // side effects must still run so state stays in sync while off-screen:
    //   - updateBubble() advances the 4s fade timer (else bubbles freeze and
    //     re-appear stale when the player returns on-screen)
    //   - debug collision box position
    if (!this.render || this.culled) {
      if (this.state.bubble) {
        this.updateBubble();
      }
      if (SHOW_COLLISION_CIRCLES && this.col) {
        this.col.position.set(this.pos.x, this.pos.y);
        this.col.rotation = this.rot;
      }
      return;
    }

    // === Base Transformations ===
    // Pre-compute shared oscillator time base (all three oscillators use the same offset)
    const timeBase = clock.time + this.randomness;

    // Breathing animation using subtle oscillation
    const breathingScale = (1 + Math.sin(timeBase / 1000) * 0.025) * this.scale;
    const rotation = this.rot;
    const shadowPos = graphicsShadowCoords(this.pos);
    const baseScale = this.state.baseScale;

    // Pre-compute sin/cos for thruster offset calculations (angle-sum identities)
    const sinRot = Math.sin(rotation);
    const cosRot = Math.cos(rotation);

    // Cache shadow scaling to avoid repeated property lookups and divisions
    const shadowScaling = SHADOW_SCALING;
    const shadowScaleMul = 4 / shadowScaling;

    // Transform main aircraft sprite
    if (this.sprites.sprite) {
      graphicsTransform(
        this.sprites.sprite,
        this.pos.x,
        this.pos.y,
        rotation,
        breathingScale * baseScale,
        breathingScale * baseScale,
      );
    }

    // Transform shadow sprite
    if (this.sprites.shadow) {
      const shadowScale = baseScale * (2.4 / shadowScaling) * this.scale;
      graphicsTransform(this.sprites.shadow, shadowPos.x, shadowPos.y, rotation, shadowScale, shadowScale);
    }

    // === Powerup Icon Transformations ===
    if (this.powerupActive) {
      // Calculate powerup scale with oscillation and fade
      const fadeIntensity =
        this.state.powerupFadeState === 0
          ? 2 * (1 - this.state.powerupFade) + 1 // Fade in: pulse larger
          : 1 - this.state.powerupFade; // Fade out: shrink
      const powerupScale = 0.35 * fadeIntensity * (1 + Math.sin(timeBase / 100) * 0.075);

      // Calculate powerup alpha
      const fadeAlpha =
        this.state.powerupFadeState === 0
          ? toolsClamp(2 * this.state.powerupFade, 0, 1) // Fade in: 0 to 1
          : toolsClamp(1 - 1.3 * this.state.powerupFade, 0, 1); // Fade out: 1 to 0
      const powerupAlpha = POWERUP_ICON_ALPHA * fadeAlpha * this.alpha;

      // Transform powerup icon (above the plane)
      if (this.sprites.powerup) {
        graphicsTransform(
          this.sprites.powerup,
          this.pos.x,
          this.pos.y - POWERUP_ICON_OFFSET_Y,
          0, // No rotation
          powerupScale,
          powerupScale,
          powerupAlpha,
        );
      }

      // Transform powerup circle (rotating, slightly larger)
      if (this.sprites.powerupCircle) {
        graphicsTransform(
          this.sprites.powerupCircle,
          this.pos.x,
          this.pos.y - POWERUP_ICON_OFFSET_Y,
          this.state.powerupAngle,
          1.35 * powerupScale,
          1.35 * powerupScale,
          powerupAlpha,
        );
      }
    }

    // === Thruster/Rotor Calculations ===
    // Oscillator for organic thruster movement
    let thrusterOscillation = 1 + Math.sin(timeBase / 0.5) * 0.1;

    // Thrust scale: amplify and add baseline (sign-aware)
    const thrustScale =
      Math.abs(this.state.thrustLevel) < 0.01
        ? 0
        : this.state.thrustLevel / 2 + (this.state.thrustLevel > 0 ? 0.5 : -0.5);

    // Thrust opacity: visible when thrusting
    const thrustOpacity = toolsClamp(2 * Math.abs(this.state.thrustLevel) - 0.1, 0, 1);

    // === Plane-Specific Thruster/Rotor Transformations ===
    switch (this.type) {
      case PlaneType.Predator: {
        // Pre-compute common thruster scale products
        const tsBase = thrusterOscillation * thrustScale * this.scale;
        const tsShadow = tsBase * shadowScaleMul;

        // Single rear thruster with glow
        const thrusterOffsetX = -sinRot * (20 * breathingScale);
        const thrusterOffsetY = cosRot * (20 * breathingScale);
        const thrusterRotation = rotation + (this.state.thrustLevel > 0 ? this.state.thrustDir : 0);

        if (this.sprites.thruster) {
          graphicsTransform(
            this.sprites.thruster,
            this.pos.x + thrusterOffsetX,
            this.pos.y + thrusterOffsetY,
            thrusterRotation,
            0.3 * tsBase,
            0.5 * tsBase,
            thrustOpacity,
          );
        }

        if (this.sprites.thrusterShadow) {
          graphicsTransform(
            this.sprites.thrusterShadow,
            shadowPos.x + thrusterOffsetX / shadowScaling,
            shadowPos.y + thrusterOffsetY / shadowScaling,
            thrusterRotation,
            0.4 * tsShadow,
            0.5 * tsShadow,
            thrustOpacity / 2.5,
          );
        }

        if (this.sprites.thrusterGlow) {
          const glowOffsetX = Math.sin(-rotation - 0.5 * this.state.thrustDir) * (40 * breathingScale);
          const glowOffsetY = Math.cos(-rotation - 0.5 * this.state.thrustDir) * (40 * breathingScale);
          graphicsTransform(
            this.sprites.thrusterGlow,
            this.pos.x + glowOffsetX,
            this.pos.y + glowOffsetY,
            null,
            2.25 * this.state.thrustLevel * this.scale,
            1.5 * this.state.thrustLevel * this.scale,
            0.3 * this.state.thrustLevel,
          );
        }
        break;
      }
      case PlaneType.Goliath:
      case PlaneType.Tornado:
      case PlaneType.Prowler: {
        // Twin flames either side of the tail, with glows; the Prowler's fade with its stealth
        const spec = DUAL_THRUSTERS[this.type];
        const alphaMul = this.type === PlaneType.Prowler ? this.alpha : 1;

        // Reduce oscillation when backing up
        if (this.state.thrustLevel < 0) {
          thrusterOscillation *= 0.7;
        }
        const tsBase = thrusterOscillation * thrustScale * this.scale;
        const tsShadow = tsBase * shadowScaleMul;
        const flameOpacity = thrustOpacity * alphaMul;
        const thrusterRotation = rotation + 0.5 * (this.state.thrustLevel > 0 ? this.state.thrustDir : 0);

        // Angle-sum identities: the left flame sits at sin(-rot - a), the right at sin(a - rot)
        const flameDist = spec.dist * breathingScale;
        const leftOffsetX = (-sinRot * spec.cos - cosRot * spec.sin) * flameDist;
        const leftOffsetY = (cosRot * spec.cos - sinRot * spec.sin) * flameDist;
        const rightOffsetX = (spec.sin * cosRot - spec.cos * sinRot) * flameDist;
        const rightOffsetY = (spec.cos * cosRot + spec.sin * sinRot) * flameDist;

        if (this.sprites.thruster1) {
          graphicsTransform(
            this.sprites.thruster1,
            this.pos.x + leftOffsetX,
            this.pos.y + leftOffsetY,
            thrusterRotation,
            spec.scaleX * tsBase,
            spec.scaleY * tsBase,
            flameOpacity,
          );
        }
        if (this.sprites.thruster2) {
          graphicsTransform(
            this.sprites.thruster2,
            this.pos.x + rightOffsetX,
            this.pos.y + rightOffsetY,
            thrusterRotation,
            spec.scaleX * tsBase,
            spec.scaleY * tsBase,
            flameOpacity,
          );
        }
        if (this.sprites.thruster1Shadow) {
          graphicsTransform(
            this.sprites.thruster1Shadow,
            shadowPos.x + leftOffsetX / shadowScaling,
            shadowPos.y + leftOffsetY / shadowScaling,
            thrusterRotation,
            spec.shadowX * tsShadow,
            spec.shadowY * tsShadow,
            flameOpacity / 2.5,
          );
        }
        if (this.sprites.thruster2Shadow) {
          graphicsTransform(
            this.sprites.thruster2Shadow,
            shadowPos.x + rightOffsetX / shadowScaling,
            shadowPos.y + rightOffsetY / shadowScaling,
            thrusterRotation,
            spec.shadowX * tsShadow,
            spec.shadowY * tsShadow,
            flameOpacity / 2.5,
          );
        }

        const glowDist = spec.glowDist * breathingScale;
        const glowAlpha = spec.glowAlpha * this.state.thrustLevel * alphaMul;
        if (this.sprites.thruster1Glow) {
          const leftGlowOffsetX = (-sinRot * spec.glowCos - cosRot * spec.glowSin) * glowDist;
          const leftGlowOffsetY = (cosRot * spec.glowCos - sinRot * spec.glowSin) * glowDist;
          graphicsTransform(
            this.sprites.thruster1Glow,
            this.pos.x + leftGlowOffsetX,
            this.pos.y + leftGlowOffsetY,
            null,
            2.5 * this.scale,
            1.5 * this.scale,
            glowAlpha,
          );
        }
        if (this.sprites.thruster2Glow) {
          const rightGlowOffsetX = (spec.glowSin * cosRot - spec.glowCos * sinRot) * glowDist;
          const rightGlowOffsetY = (spec.glowCos * cosRot + spec.glowSin * sinRot) * glowDist;
          graphicsTransform(
            this.sprites.thruster2Glow,
            this.pos.x + rightGlowOffsetX,
            this.pos.y + rightGlowOffsetY,
            null,
            2.5 * this.scale,
            1.5 * this.scale,
            glowAlpha,
          );
        }
        break;
      }
      case PlaneType.Mohawk: {
        // Rotating rotor (no thrusters)
        if (this.sprites.rotor) {
          graphicsTransform(
            this.sprites.rotor,
            this.pos.x,
            this.pos.y,
            this.state.thrustDir,
            breathingScale * baseScale * 2,
            breathingScale * baseScale * 2,
            0.8,
          );
        }

        if (this.sprites.rotorShadow) {
          const rotorShadowScale = baseScale * (2.4 / shadowScaling) * this.scale * 2;
          graphicsTransform(
            this.sprites.rotorShadow,
            shadowPos.x,
            shadowPos.y,
            this.state.thrustDir,
            rotorShadowScale,
            rotorShadowScale,
          );
        }
        break;
      }
    }

    // === Final Updates ===
    // A stale nameplate text first, so the positioning reads the new width
    this.applyStaleNameplateText();
    // Update nameplate position
    this.updateNameplate();

    // Update chat bubble if active
    if (this.state.bubble) {
      this.updateBubble();
    }

    // Update collision debug graphics if enabled
    if (SHOW_COLLISION_CIRCLES && this.col) {
      this.col.position.set(this.pos.x, this.pos.y);
      this.col.rotation = this.rot;
    }
  }

  /** Positions nameplate elements (badge, flag, name, level) relative to the player. */
  updateNameplate() {
    // The reel's planes carry no nameplate
    if (this.reel) {
      return;
    }

    // Check required sprites exist
    if (!this.sprites.name || !this.sprites.flag) {
      return;
    }

    // Pixel offsets below were tuned at the default nameplate scale (mul=1).
    // Multiplying by `m` keeps gaps proportional when `scaleNameplates` zooms the
    // whole nameplate group up/down so the flag, badge, and level plate stay glued
    // to the name text instead of drifting.
    const m = this.state.nameplateMul;

    // Calculate half-width of name and flag combined (10px spacing scales with group)
    const nameFlagHalfWidth = (this.state.nameWidth + this.sprites.flag.width + 10 * m) / 2;

    // Calculate base nameplate X position (centered, with badge and level adjustments)
    const badgeOffset = this.state.hasBadge ? 12 * m : 0;
    const levelOffset = this.sprites.level ? this.state.levelWidth / 2 + 8 * m : 0;
    const nameplateX = this.pos.x - nameFlagHalfWidth + badgeOffset - levelOffset;

    // Calculate Y position based on nameplate distance (scaled)
    const nameplateY = this.pos.y + NAMEPLATE_DIST * this.scale;

    // Position flag sprite (15px right, 10px down)
    this.sprites.flag.position.set(nameplateX + 15 * m, nameplateY + 10 * m);

    // Position name text sprite (40px right)
    this.sprites.name.position.set(nameplateX + 40 * m, nameplateY);

    // Position level plate (right edge with spacing)
    if (this.sprites.level && this.sprites.levelBorder) {
      const levelX = nameplateX + 2 * nameFlagHalfWidth + 13 * m;
      const levelY = nameplateY + (this.bot ? 3 : 2) * m; // Bots have 1px extra offset
      this.sprites.level.position.set(levelX, levelY);

      // Position level border (slightly offset for visual alignment)
      this.sprites.levelBorder.position.set(levelX - 5.25 * m, nameplateY - 0.5 * m);
    }

    // Position badge (left of flag)
    if (this.state.hasBadge && this.sprites.badge) {
      this.sprites.badge.position.set(nameplateX - 28 * m, nameplateY);
    }
  }

  /** Animates chat bubble: elastic bounce-in, 4s display, fade-out. */
  updateBubble() {
    // Check if bubble sprite exists
    if (!this.sprites.bubble) {
      return;
    }

    // Update visibility based on render flag
    this.sprites.bubble.visible = this.render;

    // Increment animation progress (elastic bounce-in)
    this.state.bubbleProgress += 0.015 * clock.timeFactor;
    if (this.state.bubbleProgress >= 1) {
      this.state.bubbleProgress = 1;
    }

    // Check if bubble should fade out (4000ms = 4 seconds)
    const bubbleAge = clock.time - this.state.bubbleTime;
    if (bubbleAge > 4000) {
      // Fade out animation
      this.state.bubbleFade += 0.08 * clock.timeFactor;
      if (this.state.bubbleFade >= 1) {
        this.state.bubbleFade = 1;
      }

      // Scale up slightly during fade
      const fadeScale = 1 + 0.2 * this.state.bubbleFade;
      this.sprites.bubble.scale.set(fadeScale);

      // Fade out alpha
      this.sprites.bubble.alpha = 1 - this.state.bubbleFade;

      // Hide bubble when fully faded
      if (this.state.bubbleFade >= 1) {
        this.state.bubble = false;
        this.sprites.bubble.visible = false;
      }
    } else {
      // Bounce-in animation (elastic easing)
      const bounceScale = toolsEasing.outElastic(this.state.bubbleProgress, 0.5);
      this.sprites.bubble.scale.set(bounceScale);

      // Fully opaque during display
      this.sprites.bubble.alpha = 1;
    }

    // Calculate pixel-perfect alignment offsets
    const pixelOffsetX = (this.state.bubbleTextWidth + viewport.width) % 2 === 0 ? 0.5 : 0;
    const pixelOffsetY = viewport.height % 2 === 0 ? 0 : 0.5;

    let bubbleOffset = NAMEPLATE_DIST * this.scale;

    // Add extra offset if powerup is active (to avoid overlap)
    if (this.powerupActive) {
      bubbleOffset += 60;
    }

    // Position bubble above nameplate
    this.sprites.bubble.position.set(
      this.pos.x * viewport.scale + pixelOffsetX,
      (this.pos.y - bubbleOffset) * viewport.scale + pixelOffsetY,
    );
  }
}

export default Player;
